import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import {
  ServiceMasterListItemModel,
  SiteMasterListItemModel,
} from '@customer-portal/data-access/global';
import { SharedSelectMultipleDatum } from '@customer-portal/shared/components/select/multiple-mod';
import { mapApiResponseToPageName } from '@customer-portal/shared/helpers';
import { convertToUtcDate } from '@customer-portal/shared/helpers/date';
import {
  CustomTreeNode,
  ServiceDetailsMaster,
  SiteDetailsMaster,
} from '@customer-portal/shared/models';

import {
  NotificationListDto,
  NotificationListItemEnrichedDto,
  NotificationsDto,
} from '../../dtos';
import { NotificationModel, NotificationSiteModel } from '../../models';

export class NotificationsListMapperService {
  static mapToNotificationListEnrichedModel(
    dto: NotificationListDto,
    domSanitizer: DomSanitizer,
    siteMasterList: SiteMasterListItemModel[],
    serviceMasterList: ServiceMasterListItemModel[],
  ): NotificationListItemEnrichedDto[] {
    if (!dto) {
      return [];
    }

    const { items } = dto.data;
    const serviceMap = new Map(serviceMasterList.map((s) => [s.id, s]));
    const siteMap = new Map(siteMasterList.map((s) => [s.id, s]));
    const companyMap = new Map(siteMasterList.map((s) => [s.companyId, s]));

    const result = items.map((notification: NotificationsDto) => {
      const companyName =
        companyMap.get(notification.accountId)?.companyName ?? '';

      const siteDetails = this.mapSiteDetails(
        notification.siteIds,
        notification.accountId,
        siteMap,
      );
      const serviceDetails = this.mapServiceDetails(
        notification.serviceIds,
        serviceMap,
      );

      return {
        infoId: notification.infoId,
        readStatus: notification.readStatus,
        subject: notification.subject,
        message:
          domSanitizer.sanitize(SecurityContext.HTML, notification.message) ??
          '',
        createdTime: notification.createdTime,
        entityType: notification.entityType,
        entityId: notification.entityId,
        certificateNumber: notification.certificateNumber,
        snowLink: notification.snowLink,
        notificationCategory: notification.notificationCategory,
        serviceIds: notification.serviceIds,
        siteIds: notification.siteIds,
        accountId: notification.accountId,
        companyName,
        siteDetails,
        serviceDetails,
        companyId: notification.accountId,
        category: notification.entityType,
      };
    });

    return result;
  }

  static recalculateState(
    notifications: NotificationListItemEnrichedDto[],
    filterCompanies: number[],
    filterServices: number[],
    filterSites: number[],
    filterCategories: string[],
    isUnreadOnly: boolean,
  ) {
    const filterCompaniesSet = new Set(filterCompanies);
    const filterServicesSet = new Set(filterServices);
    const filterSitesSet = new Set(filterSites);
    const filterCategoriesSet = new Set(filterCategories);

    const filtered = notifications.filter((item) => {
      const matchesCompany =
        !filterCompaniesSet.size || filterCompaniesSet.has(item.companyId);
      const matchesService =
        !filterServicesSet.size ||
        item.serviceDetails.some((s) => filterServicesSet.has(s.id));
      const matchesSite =
        !filterSitesSet.size ||
        item.siteDetails.some((s) => filterSitesSet.has(s.id));
      const matchesCategory =
        !filterCategoriesSet.size || filterCategoriesSet.has(item.category);
      const matchesUnreadOnly = !isUnreadOnly || item.readStatus === false;

      return (
        matchesCompany &&
        matchesService &&
        matchesSite &&
        matchesCategory &&
        matchesUnreadOnly
      );
    });

    const sortedByCreatedTimeFilteredNotifications =
      filtered.length > 0
        ? [...filtered].sort((a, b) => {
            const dateA = a.createdTime ? new Date(a.createdTime).getTime() : 0;
            const dateB = b.createdTime ? new Date(b.createdTime).getTime() : 0;

            return dateB - dateA;
          })
        : filtered;

    const dataCompanies = this.buildDataCompanies(filtered);
    const dataServices = this.buildDataServices(filtered);
    const sitesDto = this.buildNotificationFilterSitesData(filtered);
    const dataCategories = this.buildDataCategories(filtered);
    const sortedSitesDto = [...sitesDto].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    const dataSites = this.mapToFilterSites(sortedSitesDto);

    return {
      notificationItems: this.mapToNotificationListModel(
        sortedByCreatedTimeFilteredNotifications,
      ),
      dataCompanies,
      dataServices,
      dataSites,
      dataCategories,
    };
  }

  private static mapToNotificationListModel(
    notificationItems: NotificationListItemEnrichedDto[],
  ): NotificationModel[] {
    return notificationItems.map((notification) => ({
      id: notification.infoId,
      isRead: notification.readStatus,
      actionName: 'navigateFromNotification',
      title: notification.subject,
      category: notification.entityType,
      message: notification.message,
      receivedOn: convertToUtcDate(notification.createdTime),
      entityType: mapApiResponseToPageName(notification.entityType),
      entityId: notification.entityId,
      certificateNumber: notification.certificateNumber,
      snowLink: notification.snowLink,
      actions: [
        {
          actionType: 'redirect',
          iconClass: 'pi-angle-right',
          label: 'angle-right',
          url: '',
        },
      ],
      iconTooltip: {
        displayIcon: notification.readStatus === false,
        iconClass: '',
        iconPosition: '',
        tooltipMessage: 'Audit plan available',
      },
    }));
  }

  private static mapSiteDetails(
    siteIds: number[],
    companyId: number,
    siteMap: Map<number, SiteMasterListItemModel>,
  ): SiteDetailsMaster[] {
    return (siteIds || [])
      .map((siteId: number) => {
        const site = siteMap.get(siteId);

        if (site?.companyId !== companyId) {
          return undefined;
        }

        const { id, siteName, siteNickname, city, countryId, countryName } =
          site;

        return {
          id,
          siteName,
          siteNickname,
          city,
          countryId,
          countryName,
          companyId,
        };
      })
      .filter((site): site is SiteDetailsMaster => site !== undefined);
  }

  private static mapServiceDetails(
    serviceIds: number[],
    serviceMap: Map<number, ServiceMasterListItemModel>,
  ): ServiceDetailsMaster[] {
    return (serviceIds || [])
      .map((serviceId: number) => {
        const service = serviceMap.get(serviceId);

        return service
          ? {
              id: service.id,
              serviceName: service.serviceName,
            }
          : undefined;
      })
      .filter(
        (service): service is ServiceDetailsMaster => service !== undefined,
      );
  }

  private static mapToFilterSites(
    data: NotificationSiteModel[] | undefined,
    depth = 0,
    parentIds: Set<number | string> = new Set(),
  ): CustomTreeNode[] {
    return (data || []).map((datum) => {
      if (parentIds.has(datum.id)) {
        return {
          data: datum.id,
          depth,
          key: `${datum.id}-${datum.label}`,
          label: datum.label,
          siteNickname: datum.siteNickname,
          children: [],
        };
      }
      const nextParentIds = new Set(parentIds);
      nextParentIds.add(datum.id);

      return {
        data: datum.id,
        depth,
        key: `${datum.id}-${datum.label}`,
        label: datum.label,
        siteNickname: datum.siteNickname,
        children: Array.isArray(datum.children)
          ? this.mapToFilterSites(datum.children, depth + 1, nextParentIds)
          : [],
      };
    });
  }

  private static buildDataCategories(
    enrichedNotificationItems: NotificationListItemEnrichedDto[],
  ): SharedSelectMultipleDatum<string>[] {
    return Array.from(
      new Set(
        enrichedNotificationItems
          .map((item) => item.category)
          .filter((cat): cat is string => !!cat),
      ),
    )
      .map((category) => ({
        label: category,
        value: category,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private static buildDataCompanies(
    enrichedNotificationItems: NotificationListItemEnrichedDto[],
  ): SharedSelectMultipleDatum<number>[] {
    const companyMap = new Map<number, string>();
    enrichedNotificationItems.forEach((item) => {
      if (item.companyId && item.companyName) {
        companyMap.set(item.companyId, item.companyName);
      }
    });

    return Array.from(companyMap.entries())
      .map(([id, name]) => ({
        label: name,
        value: id,
      }))
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private static buildDataServices(
    enrichedNotificationItems: NotificationListItemEnrichedDto[],
  ): SharedSelectMultipleDatum<number>[] {
    const serviceMap = new Map<number, string>();
    enrichedNotificationItems.forEach((item) => {
      (item.serviceDetails || []).forEach((service) => {
        if (service?.id && service.serviceName) {
          serviceMap.set(service.id, service.serviceName);
        }
      });
    });

    return Array.from(serviceMap.entries())
      .map(([id, name]) => ({
        label: name,
        value: id,
      }))
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private static buildNotificationFilterSitesData(
    enrichedNotificationItems: NotificationListItemEnrichedDto[],
  ): NotificationSiteModel[] {
    const countryMap = new Map<string, Map<string, SiteDetailsMaster[]>>();
    let cityMap = new Map<string, SiteDetailsMaster[]>();

    enrichedNotificationItems.forEach((item) => {
      (item.siteDetails || []).forEach((site) => {
        if (!site) {
          return;
        }

        if (!site.countryName || !site.city || !site.siteName) {
          return;
        }

        if (!countryMap.has(site.countryName)) {
          countryMap.set(site.countryName, new Map());
        }
        cityMap = countryMap.get(site.countryName)!;

        if (!cityMap.has(site.city)) {
          cityMap.set(site.city, []);
        }

        if (!(cityMap.get(site.city) ?? []).some((s) => s.id === site.id)) {
          cityMap.get(site.city)?.push(site);
        }
      });
    });

    let cityIdCounter = 1000;
    const countryNodes: NotificationSiteModel[] = [];

    const sortedCountryEntries = Array.from(countryMap.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
    sortedCountryEntries.forEach(([countryName, cityMapEntry]) => {
      const firstCitySites = Array.from(cityMapEntry.values())[0];
      const countryId =
        firstCitySites?.[0]?.countryId || Math.floor(Math.random() * 100000);

      const sortedCityEntries = Array.from(cityMapEntry.entries()).sort(
        (a, b) => a[0].localeCompare(b[0]),
      );
      const cityNodes: NotificationSiteModel[] = [];
      sortedCityEntries.forEach(([cityName, sites]) => {
        const cityId = cityIdCounter;
        cityIdCounter += 1;
        const sortedSites = [...sites].sort((a, b) =>
          a.siteName.localeCompare(b.siteName),
        );
        const siteNodes: NotificationSiteModel[] = sortedSites
          .map((site) => ({
            id: site.id,
            label: site.siteName,
            siteNickname: site.siteNickname,
          }))
          .filter(Boolean);

        cityNodes.push({
          id: `city_${cityId}`,
          label: cityName,
          children: siteNodes,
        });
      });

      countryNodes.push({
        id: countryId,
        label: countryName,
        children: cityNodes,
      });
    });

    return countryNodes.filter(Boolean);
  }
}
